import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { FirstAccessVideo } from '@/types/api'
import { useAuthStore } from '@/stores/authStore'
import { dismissFirstAccess, isFirstAccessDismissed } from '@/lib/firstAccessDismissal'
import {
  FIRST_ACCESS_VIDEOS_KEY,
  fetchFirstAccessVideos,
  useFirstAccessVideos,
  useMarkFirstAccessVideoWatched,
} from './useFirstAccess'

export const WATCHED_ERROR = 'Não foi possível marcar a aula como assistida. Ela continua em Vídeo-aulas.'

/**
 * MH14-04 — a fila do pop-up em sequencia, em estado LOCAL.
 *
 * A fila e congelada na primeira resposta com `display: 'popup'`: as respostas das
 * mutations nao podem reordenar a lista nem fechar o player no meio do video.
 *
 *   watched()  "Já assisti": grava a aula e avanca (se o POST falhar, avanca com aviso;
 *              na ultima aula fica nela, com o aviso)
 *   later()    "Ver depois": avanca sem marcar; a aula segue em Vídeo-aulas
 *   close()    o X: fecha
 *
 * Depois de um "Já assisti" a fila e relida: aula encadeada (lesson_watched / curso
 * pre-requisito) que acabou de liberar entra NO FIM, sem reordenar. Na ultima aula o
 * hook espera essa releitura antes de encerrar.
 *
 * MH14-04b: passar da ultima ou fechar so esconde o pop-up ate o fim da sessao
 * (`firstAccessDismissal`); na proxima entrada o servidor manda de novo as aulas sem
 * "Já assisti".
 */
export function useFirstAccessSequence() {
  const queryClient = useQueryClient()
  const { data } = useFirstAccessVideos()
  const markWatched = useMarkFirstAccessVideoWatched()
  const userId = useAuthStore((state) => state.user?.id)
  const [frozen, setFrozen] = useState<FirstAccessVideo[] | null>(null)
  const [index, setIndex] = useState(0)
  const [finished, setFinished] = useState(() => isFirstAccessDismissed(userId))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Congela na renderizacao (padrao "estado derivado de props" do React), sem efeito.
  if (frozen === null && data?.display === 'popup' && data.videos.length > 0) {
    setFrozen(data.videos)
  }

  const queue = frozen ?? []
  const current: FirstAccessVideo | null = finished ? null : queue[index] ?? null

  const finish = () => {
    if (finished) return
    setFinished(true)
    dismissFirstAccess(userId)
  }

  const next = () => {
    setError(null)
    if (index + 1 >= queue.length) finish()
    else setIndex(index + 1)
  }

  // Aulas novas da releitura, so as que a fila ainda nao tem. Falha = nada novo.
  const unlockedAfterWatch = async (known: FirstAccessVideo[]): Promise<FirstAccessVideo[]> => {
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: FIRST_ACCESS_VIDEOS_KEY,
        queryFn: fetchFirstAccessVideos,
        staleTime: 0,
      })
      const ids = new Set(known.map((video) => video.lesson_id))
      return fresh.display === 'popup' ? fresh.videos.filter((video) => !ids.has(video.lesson_id)) : []
    } catch {
      return []
    }
  }

  const append = (additions: FirstAccessVideo[]) => {
    if (additions.length === 0) return
    setFrozen((list) => {
      const ids = new Set((list ?? []).map((video) => video.lesson_id))
      return [...(list ?? []), ...additions.filter((video) => !ids.has(video.lesson_id))]
    })
  }

  const record = async (lessonId: number): Promise<boolean> => {
    try {
      await markWatched.mutateAsync(lessonId)
      return true
    } catch {
      return false
    }
  }

  const watched = async () => {
    if (!current || saving) return
    const isLast = index + 1 >= queue.length

    if (!isLast) {
      next()
      if (await record(current.lesson_id)) append(await unlockedAfterWatch(queue))
      else setError(WATCHED_ERROR)
      return
    }

    setSaving(true)
    const ok = await record(current.lesson_id)
    const additions = ok ? await unlockedAfterWatch(queue) : []
    setSaving(false)
    if (additions.length > 0) {
      append(additions)
      setError(null)
      setIndex(index + 1)
      return
    }
    // Na ultima, a falha fica na tela (senao o aviso some com o pop-up): tentar de novo,
    // ver depois ou fechar.
    if (!ok) {
      setError(WATCHED_ERROR)
      return
    }
    finish()
  }

  return {
    current,
    position: index + 1,
    total: queue.length,
    saving,
    error,
    watched,
    later: next,
    close: finish,
  }
}
